import dayjs from 'dayjs'
import {
  IncidentReportWithCase,
  IncidentReportUpdateCaseBody,
  IncidentReportCreateCaseBody,
  IncidentReportItem
} from '@lmc/ts-common'
import { incidentReportsConstants } from 'locale/incidentReports'
import { getCombinedTimeIso } from '../utils'
import {
  IncidentReportEditCaseForm,
  IncidentReportCreateCaseForm
} from '../components/forms/IncidentCaseForm'
import { AttachmentFieldType } from '../components/UploadAttachmentsOnSaveInput'

export const prepareUpdateInitialValues = (
  incidentReport: IncidentReportWithCase,
  t: (key: string) => string
): Partial<IncidentReportEditCaseForm<string>> => {
  return {
    ...incidentReport.case,
    attachments: incidentReport.attachments.map((attachmentUrl, index) => ({
      name: `${t(incidentReportsConstants.FILE)} ${index + 1}`,
      isDeleted: false,
      isNew: false,
      url: attachmentUrl
    })),
    serviceUsersInvolved: incidentReport.case.serviceUsersInvolved.map(
      (serviceUser) => serviceUser.id
    ),
    carersInvolved: incidentReport.case.carersInvolved?.map(
      (serviceUser) => serviceUser.id
    ),
    severity: incidentReport.severity,
    log: {
      ...incidentReport.log,
      goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
      dateLogged: dayjs(incidentReport.log.timeLogged),
      timeLogged: dayjs(incidentReport.log.timeLogged),
      item: incidentReport.log.item?.id
    }
  }
}

export const prepareUpdateBody = (
  values: IncidentReportEditCaseForm<string>
): IncidentReportUpdateCaseBody => {
  return {
    severity: values.severity,
    serviceUsersInvolved: values.serviceUsersInvolved,
    otherPeopleInvolved: values.otherPeopleInvolved,
    otherWitnesses: values.otherWitnesses,
    carersInvolved: values.carersInvolved,
    physicalIntervention: values.physicalIntervention,
    restraints: values.restraints,
    chemicalRestraints: values.chemicalRestraints,
    residentInjuries: values.residentInjuries,
    carersInjuries: values.carersInjuries,
    otherPartiesInjuries: values.otherPartiesInjuries,
    damages: values.damages,
    prnMedication: values.prnMedication,
    serviceUserOpinion: values.serviceUserOpinion,
    incidentOtherInformation: values.incidentOtherInformation,
    debriefingNotes: values.debriefingNotes,
    rootCause: values.rootCause,
    preventativeActions: values.preventativeActions,
    otherNotes: values.otherNotes,
    log: {
      ...values.log,
      timeLogged: getCombinedTimeIso(
        values?.log?.dateLogged,
        values?.log?.timeLogged
      )
    },
    followUpActions: {
      ...values.followUpActions,
      actions: values?.followUpActions?.actions?.map((action) => ({
        id: action.id,
        title: action.title,
        status: action.status,
        assignedUserId: action.assignedUserId ?? null
      }))
    }
  }
}

export const getDeletedAttachmentsIndexes = (
  attachments: AttachmentFieldType[]
): number[] => {
  return attachments
    .map((attachment, index) =>
      attachment.isDeleted === true ? index : undefined
    )
    .filter((index): index is number => index !== undefined)
}

export const prepareCreateInitialValues = (
  incidentReport: IncidentReportItem
): Partial<IncidentReportCreateCaseForm<string>> => {
  return {
    ...incidentReport,
    severity: null,
    otherPeopleInvolved: null,
    physicalIntervention: { hasBeenApplied: false },
    restraints: { hasBeenApplied: false },
    chemicalRestraints: { hasBeenApplied: false },
    residentInjuries: { hasBeenApplied: false },
    carersInjuries: { hasBeenApplied: false },
    otherPartiesInjuries: { hasBeenApplied: false },
    damages: { hasBeenApplied: false },
    prnMedication: { hasBeenApplied: false },
    otherClientsInvolved: [],
    serviceUsersInvolved: [],
    attachments: [],
    followUpActions: {
      actions: [],
      notificationRequired: false,
      safeguardingActionsRequired: false
    },
    log: {
      ...incidentReport.log,
      goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
      dateLogged: dayjs(incidentReport.log.timeLogged),
      timeLogged: dayjs(incidentReport.log.timeLogged),
      item: incidentReport.log.item?.id
    }
  }
}

export const prepareCreateBody = (
  values: IncidentReportCreateCaseForm<string>
): IncidentReportCreateCaseBody => {
  return {
    ...values,
    log: {
      ...values.log,
      timeLogged: getCombinedTimeIso(
        values?.log?.dateLogged,
        values?.log?.timeLogged
      )
    },
    followUpActions: {
      ...values.followUpActions,
      actions: values?.followUpActions?.actions?.map((action) => ({
        title: action.title,
        status: action.status,
        assignedUserId: action.assignedUserId ?? null
      }))
    }
  }
}
